import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Container,
  Grid,
  CircularProgress,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Fade,
  Slide,
  Grow,
  Zoom,
  Divider,
  Stepper,
  Step,
  StepLabel,
  keyframes,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import md5 from "md5";
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Lock,
  Person,
  VerifiedUser,
  ArrowForward,
} from "@mui/icons-material";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [username, setUsername] = useState("");
  const [Otp, setOtp] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [stepDirection, setStepDirection] = useState("right");

  // Floating animation for background elements
  const floatAnimation = keyframes`
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(5deg); }
  `;

  const slideIn = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  `;

  const styles = {
    container: {
      minHeight: "100vh",
      background: `linear-gradient(135deg, rgba(95, 140, 255, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%), url("/backGround.jpg") no-repeat center center / cover`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
          "linear-gradient(45deg, rgba(95, 140, 255, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)",
        animation: `${floatAnimation} 20s ease-in-out infinite`,
      },
    },

    card: {
      background: "rgba(255, 255, 255, 0.12)",
      backdropFilter: "blur(20px)",
      borderRadius: "24px",
      border: "1px solid rgba(255, 255, 255, 0.3)",
      boxShadow: `
        0 20px 40px rgba(0, 0, 0, 0.4),
        inset 0 1px 0 rgba(255, 255, 255, 0.2)
      `,
      padding: "32px",
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "4px",
        background: "linear-gradient(90deg, #5f8cff, #8b5cf6)",
      },
    },

    backButton: {
      position: "absolute",
      top: "16px",
      left: "16px",
      background: "rgba(255, 255, 255, 0.15)",
      color: "#fff",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      zIndex: 10,
      "&:hover": {
        background: "rgba(255, 255, 255, 0.25)",
        transform: "translateX(-4px)",
        boxShadow: "0 8px 20px rgba(95, 140, 255, 0.3)",
      },
    },

    formTitle: {
      color: "#fff",
      fontSize: "28px",
      fontWeight: 700,
      marginBottom: "8px",
      textAlign: "center",
      textShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
      background: "linear-gradient(135deg, #fff, rgba(255, 255, 255, 0.8))",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      animation: `${slideIn} 0.6s ease-out`,
    },

    formSubtitle: {
      color: "#fff",
      fontSize: "14px",
      textAlign: "center",
      marginBottom: "32px",
      fontWeight: 400,
    },

    textField: {
      mb: 3,
      "& .MuiOutlinedInput-root": {
        background: "rgba(255, 255, 255, 0.12)",
        backdropFilter: "blur(12px)",
        borderRadius: "16px",
        color: "#fff",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "& fieldset": {
          borderColor: "rgba(255, 255, 255, 0.3)",
          borderWidth: "1px",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        },
        "&:hover fieldset": {
          borderColor: "rgba(255, 255, 255, 0.5)",
          boxShadow: "0 4px 20px rgba(95, 140, 255, 0.2)",
        },
        "&.Mui-focused fieldset": {
          borderColor: "#fff",
          borderWidth: "2px",
          boxShadow: "0 0 0 4px rgba(139, 92, 246, 0.25)",
        },
      },
      "& .MuiInputLabel-root": {
        color: "#fff",
        fontSize: "14px",
        fontWeight: 500,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: "#fff",
        transform: "translate(14px, -9px) scale(0.85)",
      },
      "& .MuiInputLabel-shrink": {
        transform: "translate(14px, -9px) scale(0.85)",
      },
      "& input": {
        color: "#fff",
        fontSize: "16px",
        padding: "16px",
        "&::placeholder": {
          color: "rgba(255, 255, 255, 0.5)",
        },
      },
    },

    nextButton: {
      mt: 2,
      py: 1.6,
      borderRadius: "16px",
      fontSize: "16px",
      fontWeight: 600,
      color: "#fff",
      textTransform: "none",
      background: "linear-gradient(135deg, #5f8cff, #8b5cf6)",
      boxShadow: "0 12px 30px rgba(139, 92, 246, 0.5)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "-100%",
        width: "100%",
        height: "100%",
        background:
          "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
        transition: "left 0.7s",
      },
      "&:hover": {
        background: "linear-gradient(135deg, #7aa2ff, #a78bfa)",
        boxShadow: "0 16px 40px rgba(139, 92, 246, 0.7)",
        transform: "translateY(-2px)",
        "&::before": {
          left: "100%",
        },
      },
      "&:active": {
        transform: "translateY(0)",
      },
      "&.Mui-disabled": {
        background: "rgba(255, 255, 255, 0.1)",
        color: "rgba(255, 255, 255, 0.5)",
      },
    },

    errorText: {
      color: "#ff6b6b",
      fontSize: "13px",
      marginTop: "-12px",
      marginBottom: "16px",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontWeight: 500,
      animation: `${slideIn} 0.3s ease-out`,
    },

    successText: {
      color: "#51cf66",
      fontSize: "13px",
      marginTop: "-12px",
      marginBottom: "16px",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontWeight: 500,
    },

    stepper: {
      mb: 4,
      "& .MuiStepConnector-line": {
        borderColor: "rgba(255, 255, 255, 0.2)",
      },
      "& .MuiStepLabel-label": {
        color: "#fff",
        fontWeight: 500,
        "&.Mui-active": {
          color: "#fff",
        },
        "&.Mui-completed": {
          color: "#8b5cf6",
        },
      },
      "& .MuiStepIcon-root": {
        color: "rgba(255, 255, 255, 0.2)",
        "&.Mui-active": {
          color: "#8b5cf6",
        },
        "&.Mui-completed": {
          color: "#51cf66",
        },
      },
    },

    stepIcon: {
      background: "rgba(255, 255, 255, 0.1)",
      borderRadius: "50%",
      padding: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: "12px",
    },

    floatingElement: {
      position: "absolute",
      width: "300px",
      height: "300px",
      borderRadius: "50%",
      background:
        "radial-gradient(circle, rgba(95, 140, 255, 0.1) 0%, transparent 70%)",
      animation: `${floatAnimation} 15s ease-in-out infinite`,
      zIndex: 0,
    },
  };

  const steps = ["Enter Username", "Verify OTP", "Reset Password"];

  const handleNextStep = async () => {
    if (step === 1) {
      if (!username) {
        Swal.fire({
          icon: "warning",
          title: "Warning!",
          text: "Please enter your UserName.",
          // background: "rgba(255, 255, 255, 0.1)",
          // backdropFilter: "blur(10px)",
          // color: "#fff",
          confirmButtonColor: "#8b5cf6",
        });
        return;
      }
      setLoading(true);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/Users/GetResetRequest?userName=${username}`
        );
        // Swal.fire({
        //   icon: "success",
        //   title: "Request Sent",
        //   text: "OTP sent to your registered email address.",
        //   // background: "rgba(255, 255, 255, 0.1)",
        //   // backdropFilter: "blur(10px)",
        //   // color: "#fff",
        //   confirmButtonColor: "#8b5cf6",
        // });
        // setStepDirection("right");
        // setStep(2);
        if (response.success) {
          // OTP sent successfully
          Swal.fire({
            icon: "success",
            title: "Request Sent",
            text:
              response.message || "OTP sent to your registered email address.",
            // background: "rgba(255, 255, 255, 0.1)",
            // color: "#fff",
            confirmButtonColor: "#8b5cf6",
          });

          setStepDirection("right");
          setStep(2); // move to OTP verification step
        } else {
          // Error case
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.message || "Something went wrong!",
            // background: "rgba(255, 255, 255, 0.1)",
            // color: "#fff",
            confirmButtonColor: "#f43f5e",
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "An error occurred. Please try again later.",
          // background: "rgba(255, 255, 255, 0.1)",
          // backdropFilter: "blur(10px)",
          // color: "#fff",
          confirmButtonColor: "#8b5cf6",
        });
        console.error("Error during API call:", error);
      } finally {
        setLoading(false);
      }
    } else if (step === 2) {
      if (!Otp) {
        Swal.fire({
          icon: "warning",
          title: "OTP Required",
          text: "Please enter the OTP sent to your email.",
          // background: "rgba(255, 255, 255, 0.1)",
          // backdropFilter: "blur(10px)",
          // color: "#fff",
          confirmButtonColor: "#8b5cf6",
        });
        return;
      }
      setLoading(true);
      const parsedOtp = parseInt(Otp, 10);
      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/Users/ConfirmOtp?userName=${username}&otp=${parsedOtp}`
        );
        Swal.fire({
          icon: "success",
          title: "Verification Successful",
          text: "You have been successfully verified!",
          // background: "rgba(255, 255, 255, 0.1)",
          // backdropFilter: "blur(10px)",
          // color: "#fff",
          confirmButtonColor: "#8b5cf6",
        });
        setStepDirection("right");
        setStep(3);
      } catch (error) {
        console.error("Error:", error);
        Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text: "Invalid or expired OTP. Please try again.",
          // background: "rgba(255, 255, 255, 0.1)",
          // backdropFilter: "blur(10px)",
          // color: "#fff",
          confirmButtonColor: "#8b5cf6",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const validateFields = () => {
    let isValid = true;

    if (!password) {
      setPasswordError("Password is required");
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters long");
      isValid = false;
    } else {
      setPasswordError("");
    }

    if (!confirmPassword) {
      setConfirmPasswordError("Please confirm your password");
      isValid = false;
    } else if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      isValid = false;
    } else {
      setConfirmPasswordError("");
    }

    return isValid;
  };

  const handlePasswordReset = async () => {
    if (!validateFields()) return;
    setLoading(true);
    const hashedPassword = md5(password).toString();
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/Users/Reset-Password?username=${username}&password=${hashedPassword}`
      );
      Swal.fire({
        icon: "success",
        title: "Password Reset Successful",
        text: "You can now log in with your new password.",
        // background: "rgba(255, 255, 255, 0.1)",
        // backdropFilter: "blur(10px)",
        // color: "#fff",
        confirmButtonColor: "#8b5cf6",
      });
      navigate("/login");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while resetting your password. Please try again.",
        // background: "rgba(255, 255, 255, 0.1)",
        // backdropFilter: "blur(10px)",
        // color: "#fff",
        confirmButtonColor: "#8b5cf6",
      });
      console.error("Error during API call:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 1) {
      navigate("/login");
    } else {
      setStepDirection("left");
      setStep(step - 1);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (step === 1) handleNextStep();
      else if (step === 2) handleNextStep();
      else if (step === 3) handlePasswordReset();
    }
  };

  return (
    <Container maxWidth={false} sx={styles.container}>
      {/* Floating background elements */}
      <Box sx={{ ...styles.floatingElement, top: "10%", left: "10%" }} />
      <Box
        sx={{
          ...styles.floatingElement,
          bottom: "10%",
          right: "10%",
          animationDelay: "2s",
        }}
      />

      <Container maxWidth="lg" sx={{ zIndex: 1 }}>
        <Grid container justifyContent="center" alignItems="center">
          <Grid item xs={12} sm={10} md={8} lg={6} xl={5}>
            <Slide direction="up" in={true} mountOnEnter unmountOnExit>
              <Card sx={styles.card}>
                <CardContent sx={{ position: "relative", pt: 6 }}>
                  {/* Back Button */}
                  <Zoom in={true}>
                    <IconButton onClick={handleBack} sx={styles.backButton}>
                      <ArrowBackIcon />
                    </IconButton>
                  </Zoom>

                  {/* Stepper */}
                  <Stepper
                    activeStep={step - 1}
                    alternativeLabel
                    sx={styles.stepper}
                  >
                    {steps.map((label) => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>

                  {/* Step 1: Enter Username */}
                  <Slide
                    direction={stepDirection}
                    in={step === 1}
                    mountOnEnter
                    unmountOnExit
                  >
                    <Box>
                      <Typography sx={styles.formTitle}>
                        Reset Your Password
                      </Typography>
                      <Typography sx={styles.formSubtitle}>
                        Enter your username to receive a verification code
                      </Typography>

                      <TextField
                        fullWidth
                        label="Username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        onKeyPress={handleKeyPress}
                        sx={styles.textField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person
                                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <Button
                        fullWidth
                        onClick={handleNextStep}
                        sx={styles.nextButton}
                        disabled={loading}
                        endIcon={!loading && <ArrowForward />}
                      >
                        {loading ? (
                          <CircularProgress size={24} sx={{ color: "#fff" }} />
                        ) : (
                          "Send OTP"
                        )}
                      </Button>
                    </Box>
                  </Slide>

                  {/* Step 2: Verify OTP */}
                  <Slide
                    direction={stepDirection}
                    in={step === 2}
                    mountOnEnter
                    unmountOnExit
                  >
                    <Box>
                      <Typography sx={styles.formTitle}>
                        Verify Your Identity
                      </Typography>
                      <Typography sx={styles.formSubtitle}>
                        Enter the OTP sent to your registered email
                      </Typography>

                      <TextField
                        fullWidth
                        label="Username"
                        value={username}
                        // InputProps={{ readOnly: true }}
                        sx={styles.textField}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person
                                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <TextField
                        fullWidth
                        label="Verification Code"
                        value={Otp}
                        onChange={(e) => setOtp(e.target.value)}
                        onKeyPress={handleKeyPress}
                        sx={styles.textField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <VerifiedUser
                                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <Button
                        fullWidth
                        onClick={handleNextStep}
                        sx={styles.nextButton}
                        disabled={loading}
                        endIcon={!loading && <CheckCircle />}
                      >
                        {loading ? (
                          <CircularProgress size={24} sx={{ color: "#fff" }} />
                        ) : (
                          "Verify & Continue"
                        )}
                      </Button>
                    </Box>
                  </Slide>

                  {/* Step 3: Reset Password */}
                  <Slide
                    direction={stepDirection}
                    in={step === 3}
                    mountOnEnter
                    unmountOnExit
                  >
                    <Box>
                      <Typography sx={styles.formTitle}>
                        Create New Password
                      </Typography>
                      <Typography sx={styles.formSubtitle}>
                        Enter a strong password to secure your account
                      </Typography>

                      <TextField
                        fullWidth
                        label="New Password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyPress={handleKeyPress}
                        sx={styles.textField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock
                                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                onMouseDown={(e) => e.preventDefault()}
                                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                              >
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {passwordError && (
                        <Typography sx={styles.errorText}>
                          {passwordError}
                        </Typography>
                      )}
                      {password && !passwordError && (
                        <Typography sx={styles.successText}>
                          <CheckCircle fontSize="small" />
                          Password strength: Good
                        </Typography>
                      )}

                      <TextField
                        fullWidth
                        label="Confirm Password"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        onKeyPress={handleKeyPress}
                        sx={styles.textField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock
                                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                onMouseDown={(e) => e.preventDefault()}
                                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                              >
                                {showConfirmPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {confirmPasswordError && (
                        <Typography sx={styles.errorText}>
                          {confirmPasswordError}
                        </Typography>
                      )}
                      {confirmPassword &&
                        !confirmPasswordError &&
                        password === confirmPassword && (
                          <Typography sx={styles.successText}>
                            <CheckCircle fontSize="small" />
                            Passwords match
                          </Typography>
                        )}

                      <Button
                        fullWidth
                        onClick={handlePasswordReset}
                        sx={styles.nextButton}
                        disabled={loading}
                        endIcon={!loading && <CheckCircle />}
                      >
                        {loading ? (
                          <CircularProgress size={24} sx={{ color: "#fff" }} />
                        ) : (
                          "Reset Password"
                        )}
                      </Button>
                    </Box>
                  </Slide>

                  {/* Progress Indicator */}
                  <Box sx={{ mt: 3, textAlign: "center" }}>
                    <Typography sx={{ color: "#fff", fontSize: "12px" }}>
                      Step {step} of {steps.length}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Slide>
          </Grid>
        </Grid>
      </Container>
    </Container>
  );
};

export default ResetPassword;
